import { useEffect, useState } from 'react';
import { Box, Flex, Text, Button, Image, Spinner } from '@chakra-ui/react';
import { UserPlus } from 'lucide-react';
import { useOrganizationMembershipRequests } from '@/hooks/useOrganizationMembershipRequests';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';
import { MEMBERS_LIST_MAX_HEIGHT } from '../OrganizationMembersSection/OrganizationMembersSection';

function displayName(r: { firstName: string | null; lastName: string | null }): string {
  const n = [r.firstName, r.lastName].filter(Boolean).join(' ').trim();
  return n || 'Volunteer';
}

type Props = {
  organizationUserId: string;
  refreshSignal?: number;
};

export function PendingMembershipRequestsSection({
  organizationUserId,
  refreshSignal = 0,
}: Props) {
  const {
    pendingRequests,
    loading,
    error,
    acceptMembership,
    rejectMembership,
    refetch,
  } = useOrganizationMembershipRequests(organizationUserId);
  const { showToast, hideToast, toast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!refreshSignal) return;
    refetch();
  }, [refreshSignal, refetch]);

  const run = async (
    membershipId: string,
    action: 'accept' | 'reject'
  ) => {
    setBusyId(membershipId);
    try {
      const fn = action === 'accept' ? acceptMembership : rejectMembership;
      const r = await fn(membershipId);
      if (!r.ok && r.message) showToast(r.message, 'error');
      else if (r.ok) {
        showToast(action === 'accept' ? 'Request accepted.' : 'Request declined.', 'success');
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box
      as="section"
      mt="4"
      bg="white"
      border="1px solid"
      borderColor="volyx.mediumGray"
      borderRadius="24px"
      px="5"
      py="4"
      boxShadow="0 2px 12px rgba(0,0,0,0.06)"
    >
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <Flex align="center" gap="3" mb="4">
        <Box
          w="44px"
          h="44px"
          borderRadius="full"
          bg="volyx.primaryLight"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <UserPlus size={20} color="#007AFF" aria-hidden />
        </Box>
        <Box minW="0" flex="1">
          <Text m="0" fontSize="sm" fontWeight="bold" color="volyx.text" lineClamp={1}>
            Membership requests
          </Text>
          <Text m="0" mt="0.5" fontSize="xs" color="volyx.textSecondary">
            Accept or decline volunteers who asked to join your organization.
          </Text>
        </Box>
      </Flex>

      {loading && (
        <Flex justify="center" py="6">
          <Spinner color="volyx.primary" size="md" />
        </Flex>
      )}

      {!loading && error && (
        <Text fontSize="sm" color="red.500" m="0">
          {error}
        </Text>
      )}

      {!loading && !error && pendingRequests.length === 0 && (
        <Text fontSize="sm" color="volyx.textSecondary" m="0" py="2">
          No pending requests. When volunteers request to join from the marketplace, they will appear
          here.
        </Text>
      )}

      {!loading && !error && pendingRequests.length > 0 && (
        <Box
          maxH={MEMBERS_LIST_MAX_HEIGHT}
          overflowY="auto"
          overflowX="hidden"
          pr="1"
          css={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Flex direction="column" gap="3">
            {pendingRequests.map((req, index) => {
              const busy = busyId === req.membershipId;
              return (
                <Flex
                  key={req.membershipId}
                  align="center"
                  gap="3"
                  flexWrap="wrap"
                  borderTop={index === 0 ? undefined : '1px solid'}
                  borderColor="gray.100"
                  pt={index === 0 ? 0 : 3}
                >
                  <Image
                    src={req.profileImage || DEFAULT_PROFILE_IMAGE}
                    alt=""
                    boxSize="44px"
                    borderRadius="full"
                    objectFit="cover"
                    flexShrink={0}
                    border="1px solid"
                    borderColor="volyx.mediumGray"
                  />
                  <Box minW="0" flex="1">
                    <Text m="0" fontSize="sm" fontWeight="semibold" color="volyx.text" lineClamp={1}>
                      {displayName(req)}
                    </Text>
                    <Text m="0" fontSize="xs" color="volyx.textSecondary">
                      Requested{' '}
                      {new Date(req.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                  </Box>
                  <Flex gap="2" flexShrink={0}>
                    <Button
                      h="40px"
                      px="4"
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="red.200"
                      bg="red.50"
                      color="red.600"
                      fontSize="sm"
                      fontWeight="semibold"
                      boxShadow="0 4px 12px rgba(0,0,0,0.08)"
                      transition="all 0.2s"
                      _hover={{ bg: 'red.100', transform: 'translateY(-1px)', boxShadow: '0 8px 18px rgba(0,0,0,0.12)' }}
                      _active={{ transform: 'translateY(0)' }}
                      _disabled={{ opacity: 0.6, cursor: 'not-allowed', transform: 'none' }}
                      disabled={busy}
                      onClick={() => run(req.membershipId, 'reject')}
                    >
                      Decline
                    </Button>
                    <Button
                      h="40px"
                      px="4"
                      borderRadius="xl"
                      bg="green.500"
                      color="white"
                      fontSize="sm"
                      fontWeight="semibold"
                      boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                      transition="all 0.2s"
                      _hover={{ bg: 'green.600', transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}
                      _active={{ transform: 'translateY(0)' }}
                      _disabled={{ opacity: 0.6, cursor: 'not-allowed', transform: 'none' }}
                      disabled={busy}
                      onClick={() => run(req.membershipId, 'accept')}
                    >
                      {busy ? '…' : 'Accept'}
                    </Button>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}

      <Text m="0" mt="4" fontSize="xs" color="volyx.textSecondary">
        Invite link for your organization — coming soon
      </Text>
    </Box>
  );
}
